import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import useAIFeedbackStore from "@features/session/hooks/useAIFeedbackStore";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import type { ItemResponse } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import ProfileIcon from "@memorang/ui/components/ChatUI/ProfileIcon";
import SheetHandle from "@memorang/ui/components/SheetHandle";
import type { IconType } from "@memorang/ui/icon";
import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable } from "react-native";
import { ActivityIndicator, Text } from "react-native-paper";
import { getNormalizedScore } from "../../helpers/ai-feedback";

const positivePhrases = [
	"session.feedback.greatJob",
	"session.feedback.niceWork",
	"session.feedback.wellDone",
	"session.feedback.excellent",
	"session.feedback.outstanding",
	"session.feedback.fantastic",
	"session.feedback.brilliant",
	"session.feedback.amazingWork",
	"session.feedback.superb",
	"session.feedback.wayToGo",
	"session.feedback.perfect",
	"session.feedback.impressive",
	"session.feedback.stellarWork",
	"session.feedback.awesomeJob",
	"session.feedback.excellentEffort",
	"session.feedback.remarkable",
	"session.feedback.youNailedIt",
	"session.feedback.terrificWork",
];

const negativePhrases = [
	"session.feedback.negative.hangIn",
	"session.feedback.negative.youCanDoIt",
	"session.feedback.negative.keepTrying",
	"session.feedback.negative.persistenceKey",
	"session.feedback.negative.practiceMakesPerfect",
	"session.feedback.negative.keepGoingStrong",
];

type Props = {
	handleExpandCollapse?: () => void;
	itemId: number;
	responseType: ItemResponse["type"];
	maximumScore: number;
};

type LoadingState = {
	icon: IconType;
	nameKey: string;
};

const STATES = {
	AUDIO: {
		INITIAL: {
			icon: "microphone-outline",
			nameKey: "session.aiStates.processingAudio",
		},
		ANALYZING: {
			icon: "waveform",
			nameKey: "session.aiStates.analyzingSpeech",
		},
		GRADING: {
			icon: "clipboard-check-outline",
			nameKey: "session.aiStates.gradingResponse",
		},
		FEEDBACK: {
			icon: "message-text-outline",
			nameKey: "session.aiStates.generatingFeedback",
		},
	},
	WRITING: {
		INITIAL: {
			icon: "text-box-outline",
			nameKey: "session.aiStates.processingText",
		},
		GRADING: {
			icon: "clipboard-check-outline",
			nameKey: "session.aiStates.gradingResponse",
		},
		FEEDBACK: {
			icon: "message-text-outline",
			nameKey: "session.aiStates.generatingFeedback",
		},
	},
	COMPLETE: { icon: "thumb-up-outline", nameKey: "" },
} as const;

const AiModalHandle = ({
	handleExpandCollapse,
	itemId,
	responseType,
	maximumScore,
}: Props) => {
	const theme = useAppTheme();
	const aiAssistant = useAppStore((store) => store.app.aiAssistant);
	const { t } = useTranslation();

	const aiFeedback = useAIFeedbackStore((store) => store.aiResponse?.[itemId]);
	const streaming = useAIFeedbackStore((store) => store.streaming);

	const [currentStatus, setCurrentStatus] = useState<LoadingState>(
		responseType === "recording"
			? STATES.AUDIO.INITIAL
			: STATES.WRITING.INITIAL,
	);

	const randomPositivePhrase = useMemo(() => {
		const normalizedScore = getNormalizedScore(
			aiFeedback?.score ?? 0,
			maximumScore,
		);
		if (normalizedScore < 50) {
			return t(
				negativePhrases[Math.floor(Math.random() * negativePhrases.length)],
			);
		}
		return t(
			positivePhrases[Math.floor(Math.random() * positivePhrases.length)],
		);
	}, [aiFeedback?.score, maximumScore, t]);

	useEffect(() => {
		if (!streaming) {
			setCurrentStatus({ ...STATES.COMPLETE, nameKey: randomPositivePhrase });
			return;
		}

		if (!aiFeedback) {
			return;
		}

		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		const updateStatus = () => {
			if (responseType === "recording") {
				if (aiFeedback.explanation?.length) {
					return STATES.AUDIO.FEEDBACK;
				}
				if (aiFeedback.subskillAssessments?.length) {
					return STATES.AUDIO.GRADING;
				}
				return STATES.AUDIO.ANALYZING;
			}

			if (aiFeedback.explanation?.length) {
				return STATES.WRITING.FEEDBACK;
			}
			if (aiFeedback.subskillAssessments?.length) {
				return STATES.WRITING.GRADING;
			}
			return STATES.WRITING.INITIAL;
		};

		const newStatus = updateStatus();

		if (currentStatus.nameKey === newStatus.nameKey) {
			return;
		}
		setCurrentStatus(newStatus);
	}, [
		streaming,
		responseType,
		randomPositivePhrase,
		aiFeedback,
		currentStatus.nameKey,
	]);

	const icon = useMemo(
		() =>
			currentStatus.icon !== STATES.COMPLETE.icon ? (
				<Box justifyContent="center" alignItems="center">
					<ActivityIndicator animating color={theme.colors.primary} size={24} />
					<Icon
						name={currentStatus.icon}
						size={14}
						color={theme.colors.text}
						style={{ position: "absolute" }}
					/>
				</Box>
			) : (
				<Icon name={currentStatus.icon} size={24} color={theme.colors.text} />
			),
		[currentStatus.icon, theme.colors.primary, theme.colors.text],
	);

	return (
		<Pressable onPress={handleExpandCollapse} disabled={!handleExpandCollapse}>
			<Box
				width="100%"
				backgroundColor={theme.palette.success.info}
				style={
					isWeb ? {} : { borderTopLeftRadius: 24, borderTopRightRadius: 24 }
				}
			>
				{!isWeb && (
					<SheetHandle
						onPress={handleExpandCollapse}
						backgroundColor={theme.colors.border}
					/>
				)}
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
					paddingHorizontal={16}
					paddingBottom={isWeb ? 16 : 20}
					paddingTop={isWeb ? 16 : 0}
				>
					<Box flexDirection="row" alignItems="center" gap={8}>
						{icon}
						<Text variant="titleMedium">{t(currentStatus.nameKey)}</Text>
					</Box>
					<Box flexDirection="row" alignItems="center" gap={8}>
						<ProfileIcon uri={aiAssistant.icon} />
						<Text variant="titleMedium">{aiAssistant.alias}</Text>
					</Box>
				</Box>
			</Box>
		</Pressable>
	);
};

export default memo(AiModalHandle);
